import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, PanelRightClose, Clock, Info, Loader2, ChevronRight, Check, AlertCircle, TrendingUp, Sparkles } from 'lucide-react';
import { PanelSkeleton } from '../shared/Skeleton';
import toast from 'react-hot-toast';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import { DriverBadge } from '../shared/DriverBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { CascadeOptionsModal } from './CascadeOptionsModal';
import { DepartmentAnalysisTab } from './DepartmentAnalysisTab';
import { AiRoomsForecastModal } from '../ai-build/AiRoomsForecastModal';
import { showAiUndoToast } from '../ai-build/aiUndoToast';
import {
  getDepartmentDriver,
  saveDepartmentDriver,
  clearDepartmentDriver,
  getDepartmentAccounts,
  getRevenueDepartments,
  getRoomForecastSources,
} from '../../api';
import { getVersionDriverEntry } from './driverCatalog';
import { DRIVER_CONFIG } from '../../types';
import type {
  DepartmentDriver,
  DepartmentDriverType,
  DepartmentAccountInfo,
  CascadeOptions,
  FallbackDriverType,
  RoomForecastSource,
} from '../../api';
import type { DetailPanelTab, DriverType } from '../../types';
// @ts-ignore - JavaScript module
import { useUserSettingsContext } from '../../../../context/UserSettingsContext';

// Fallback driver options for future months
const FALLBACK_DRIVER_OPTIONS: { value: FallbackDriverType; label: string; description: string }[] = [
  {
    value: null,
    label: 'None',
    description: 'No fallback - even distribution of OTB/RMS',
  },
  {
    value: 'LY Same Month',
    label: 'LY Same Month',
    description: 'Use last year actual for same month',
  },
  {
    value: 'Budget',
    label: 'Budget',
    description: 'Use budgeted amount',
  },
];

interface DepartmentDetailPanelProps {
  /** Rendered in a popped-out window (full width, dock control). */
  isPoppedOut?: boolean;
  onTogglePopout?: () => void;
  departmentName: string;
  departmentId?: number;  // FK to chartOfAccountDepartments (if available)
  parentSection: 'Revenue' | 'Expense';
  onClose: () => void;
  versionId: number;
  locationId: number;
  isReadOnly?: boolean;
  onDriverSaved?: () => void;
  onRevenueForecastSetup?: () => void;  // Revenue departments only — opens Revenue Forecast modal
  occupiedRoomsPerMonth?: Record<string, number>;
  currency?: string | null;
}

// Driver types that require a percentage input
const PERCENTAGE_DRIVERS = ['% of Revenue', '% of Selected Revenue'];

// Legacy department drivers display as 'Room Forecast' with the equivalent
// source pre-selected (pre-migration rows keep working unchanged).
const LEGACY_ROOM_FORECAST_SOURCES: Record<string, string> = {
  'OTB Forecast': 'otb',
  'RMS Forecast': 'projected',
};
const displayDriverType = (driverType: string | undefined | null): string | null =>
  driverType ? (LEGACY_ROOM_FORECAST_SOURCES[driverType] ? 'Room Forecast' : driverType) : null;
const driverForecastSource = (driver: DepartmentDriver | null): string =>
  driver?.forecastSource
  || (driver ? LEGACY_ROOM_FORECAST_SOURCES[driver.driverType] : undefined)
  || 'otb';

// Driver options for department level
// All these drivers distribute values proportionally among accounts based on past performance.
const DEPARTMENT_DRIVER_OPTIONS: { type: DepartmentDriverType; label: string; description: string; requiresInput?: boolean }[] = [
  {
    type: 'Room Forecast',
    label: 'Room Forecast',
    description: 'Distribute the selected rooms forecast (on the books, RMS, BI, user forecast, ...) proportionally across accounts',
  },
  {
    type: '% of Revenue',
    label: '% of Revenue',
    description: 'Distribute percentage of total revenue proportionally across accounts',
    requiresInput: true,
  },
  {
    type: '% of Selected Revenue',
    label: '% of Selected Revenue',
    description: 'Distribute percentage of selected department revenue proportionally',
    requiresInput: true,
  },
];

export function DepartmentDetailPanel({
  isPoppedOut = false,
  onTogglePopout,
  departmentName,
  departmentId,
  parentSection,
  onClose,
  versionId,
  locationId,
  isReadOnly = false,
  onDriverSaved,
  onRevenueForecastSetup,
  occupiedRoomsPerMonth,
  currency = 'USD',
}: DepartmentDetailPanelProps) {
  const { state, setDetailPanelTab } = useForecastV3();
  const { detailPanelTab, orgId, year, currentVersion } = state;

  // The AI Rooms Forecast (OTB + learned pickup) is a forecasting tool — it
  // projects from what's currently on the books, which has no meaning for a
  // future-year budget build.
  const isBudgetVersion = currentVersion?.type === 'Budget';

  // Department driver state
  const [departmentDriver, setDepartmentDriver] = useState<DepartmentDriver | null>(null);
  const [accounts, setAccounts] = useState<DepartmentAccountInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Selected driver for pending change
  const [pendingDriverType, setPendingDriverType] = useState<DepartmentDriverType | null>(null);
  const [showCascadeModal, setShowCascadeModal] = useState(false);

  // AI Rooms Forecast modal (Revenue departments only)
  const [showAiRoomsForecast, setShowAiRoomsForecast] = useState(false);

  // State for percentage driver input
  const [driverInput, setDriverInput] = useState<string>('');

  // Local state for lead time configuration
  const [localLeadDays, setLocalLeadDays] = useState<number>(5);
  const [localFallbackDriver, setLocalFallbackDriver] = useState<FallbackDriverType>(null);
  const [hasUnsavedConfig, setHasUnsavedConfig] = useState(false);

  // Room Forecast source selection + available sources for this location
  const [localForecastSource, setLocalForecastSource] = useState<string>('otb');
  const [roomForecastSources, setRoomForecastSources] = useState<RoomForecastSource[]>([]);

  // State for "% of Selected Revenue" department selection
  const [revenueDepartments, setRevenueDepartments] = useState<RevenueDepartment[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  // Sync local state when department driver loads/changes
  useEffect(() => {
    if (departmentDriver) {
      setLocalLeadDays(Math.abs(departmentDriver.otbLeadDays ?? -5));
      setLocalFallbackDriver(departmentDriver.fallbackDriverType ?? null);
      setDriverInput(departmentDriver.driverInput !== null ? String(departmentDriver.driverInput) : '');
      setSelectedDepartments(departmentDriver.selectedDepartments || []);
      setLocalForecastSource(driverForecastSource(departmentDriver));
      setHasUnsavedConfig(false);
    }
  }, [departmentDriver]);

  // Available Room Forecast sources for this location (Revenue departments only)
  useEffect(() => {
    if (parentSection === 'Revenue' && locationId) {
      getRoomForecastSources(locationId, versionId)
        .then(setRoomForecastSources)
        .catch((err) => {
          console.error('Failed to fetch room forecast sources:', err);
          // Fall back to the two always-available slots
          setRoomForecastSources([
            { value: 'otb', label: 'On The Books', description: 'Actuals to date plus rooms on the books' },
            { value: 'projected', label: 'System Projection', description: 'The location mapped projection (RMS/PMS forecast)' },
          ]);
        });
    }
  }, [parentSection, locationId, versionId]);

  // Check if current driver type requires department selection
  const requiresDepartmentSelection = departmentDriver?.driverType === '% of Selected Revenue' ||
    pendingDriverType === '% of Selected Revenue';

  // Fetch revenue departments when needed
  useEffect(() => {
    if (requiresDepartmentSelection && orgId && locationId && revenueDepartments.length === 0) {
      setIsLoadingDepartments(true);
      getRevenueDepartments({ orgId, locationId })
        .then((response) => {
          setRevenueDepartments(response.departments || []);
        })
        .catch((err) => {
          console.error('Failed to fetch revenue departments:', err);
          toast.error('Failed to load revenue departments');
        })
        .finally(() => {
          setIsLoadingDepartments(false);
        });
    }
  }, [requiresDepartmentSelection, orgId, locationId, revenueDepartments.length]);

  const tabs: DetailPanelTab[] = ['driver', 'adjustments', 'analysis', 'overrides', 'notes'];

  // Get the active month for the analysis tab
  const { activeMonth } = useMonthlyVersions();

  // Fetch department driver and accounts
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [driver, accountList] = await Promise.all([
        getDepartmentDriver({ versionId, locationId, departmentName, chartOfAccountDepartmentId: departmentId, accountType: parentSection }),
        getDepartmentAccounts({ versionId, locationId, departmentName, chartOfAccountDepartmentId: departmentId, accountType: parentSection }),
      ]);
      setDepartmentDriver(driver);
      setAccounts(accountList);
    } catch (error) {
      console.error('Failed to fetch department data:', error);
      toast.error('Failed to load department data');
    } finally {
      setIsLoading(false);
    }
  }, [versionId, locationId, departmentName, departmentId, parentSection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle driver selection
  const handleDriverSelect = (driverType: DepartmentDriverType) => {
    if (isReadOnly) return;

    // Check if any accounts have conflicting data (including rooms forecast daily values)
    const hasConflicts = accounts.some(
      a => a.hasAccountDriver || a.hasOverrides || a.hasAdjustments || a.hasDailyValues
    );

    if (hasConflicts) {
      setPendingDriverType(driverType);
      setShowCascadeModal(true);
    } else {
      // No conflicts, apply directly
      applyDriver(driverType, {
        clearAccountDrivers: false,
        clearAccountOverrides: false,
        clearAccountAdjustments: false,
        clearAccountNotes: false,
      });
    }
  };

  // Apply driver with cascade options
  const applyDriver = async (driverType: DepartmentDriverType, cascadeOptions: CascadeOptions) => {
    setIsSaving(true);
    try {
      // Parse driver input for percentage drivers
      const inputValue = PERCENTAGE_DRIVERS.includes(driverType) && driverInput
        ? parseFloat(driverInput)
        : null;

      const result = await saveDepartmentDriver({
        versionId,
        locationId,
        departmentName,
        chartOfAccountDepartmentId: departmentId,
        accountType: parentSection,
        driverType,
        driverInput: inputValue,
        otbLeadDays: -Math.abs(localLeadDays),  // Store as negative
        fallbackDriverType: localFallbackDriver,
        selectedDepartments: driverType === '% of Selected Revenue' ? selectedDepartments : undefined,
        forecastSource: driverType === 'Room Forecast' ? localForecastSource : undefined,
        cascadeOptions,
      });

      setDepartmentDriver(result.departmentDriver);
      setPendingDriverType(null);
      setShowCascadeModal(false);

      const affectedMsg = result.affectedAccounts
        ? ` (${result.affectedAccounts} accounts affected)`
        : '';
      toast.success(`Department driver set to ${driverType}${affectedMsg}`);

      // Refresh accounts to show updated inheritance status
      const accountList = await getDepartmentAccounts({ versionId, locationId, departmentName, chartOfAccountDepartmentId: departmentId, accountType: parentSection });
      setAccounts(accountList);

      onDriverSaved?.();
    } catch (error) {
      console.error('Failed to save department driver:', error);
      toast.error('Failed to save department driver');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle clear driver
  const handleClearDriver = async () => {
    if (isReadOnly || !departmentDriver) return;

    setIsSaving(true);
    try {
      await clearDepartmentDriver({ versionId, locationId, departmentName, chartOfAccountDepartmentId: departmentId, accountType: parentSection });
      setDepartmentDriver(null);
      toast.success('Department driver cleared');

      // Refresh accounts
      const accountList = await getDepartmentAccounts({ versionId, locationId, departmentName, chartOfAccountDepartmentId: departmentId, accountType: parentSection });
      setAccounts(accountList);

      onDriverSaved?.();
    } catch (error) {
      console.error('Failed to clear department driver:', error);
      toast.error('Failed to clear department driver');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to compare arrays for equality
  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  // Check if config has unsaved changes
  const checkUnsavedConfig = (
    leadDays: number,
    fallback: FallbackDriverType,
    input: string,
    departments: string[],
    forecastSource?: string
  ) => {
    if (!departmentDriver) return false;
    const currentLeadDays = Math.abs(departmentDriver.otbLeadDays ?? -5);
    const currentInput = departmentDriver.driverInput !== null ? String(departmentDriver.driverInput) : '';
    const currentDepartments = departmentDriver.selectedDepartments || [];

    return (
      leadDays !== currentLeadDays ||
      fallback !== departmentDriver.fallbackDriverType ||
      input !== currentInput ||
      !arraysEqual(departments, currentDepartments) ||
      (forecastSource ?? localForecastSource) !== driverForecastSource(departmentDriver)
    );
  };

  // Save configuration changes (lead time, fallback, percentage, departments)
  const handleSaveConfig = async () => {
    if (isReadOnly || !departmentDriver) return;

    setIsSaving(true);
    try {
      // Parse driver input for percentage drivers
      const inputValue = PERCENTAGE_DRIVERS.includes(departmentDriver.driverType) && driverInput
        ? parseFloat(driverInput)
        : null;

      // Legacy OTB/RMS rows re-save as the unified Room Forecast driver with
      // the equivalent (or newly selected) source.
      const savedDriverType = (displayDriverType(departmentDriver.driverType) || departmentDriver.driverType) as DepartmentDriverType;
      const result = await saveDepartmentDriver({
        versionId,
        locationId,
        departmentName,
        chartOfAccountDepartmentId: departmentId,
        accountType: parentSection,
        driverType: savedDriverType,
        driverInput: inputValue,
        otbLeadDays: -Math.abs(localLeadDays),
        fallbackDriverType: localFallbackDriver,
        selectedDepartments: departmentDriver.driverType === '% of Selected Revenue' ? selectedDepartments : undefined,
        forecastSource: savedDriverType === 'Room Forecast' ? localForecastSource : undefined,
      });

      setDepartmentDriver(result.departmentDriver);
      setHasUnsavedConfig(false);
      toast.success('Configuration saved');
      onDriverSaved?.();
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle local config changes
  const handleLeadDaysChange = (value: number) => {
    setLocalLeadDays(value);
    setHasUnsavedConfig(checkUnsavedConfig(value, localFallbackDriver, driverInput, selectedDepartments));
  };

  const handleFallbackChange = (value: FallbackDriverType) => {
    setLocalFallbackDriver(value);
    setHasUnsavedConfig(checkUnsavedConfig(localLeadDays, value, driverInput, selectedDepartments));
  };

  const handleForecastSourceChange = (value: string) => {
    setLocalForecastSource(value);
    setHasUnsavedConfig(checkUnsavedConfig(localLeadDays, localFallbackDriver, driverInput, selectedDepartments, value));
  };

  const handleDriverInputChange = (value: string) => {
    setDriverInput(value);
    setHasUnsavedConfig(checkUnsavedConfig(localLeadDays, localFallbackDriver, value, selectedDepartments));
  };

  const handleSelectedDepartmentsChange = (departments: string[]) => {
    setSelectedDepartments(departments);
    setHasUnsavedConfig(checkUnsavedConfig(localLeadDays, localFallbackDriver, driverInput, departments));
  };

  // Count inheritance status
  const inheritingCount = accounts.filter(a => a.inheritsFromDepartment).length;
  const overriddenCount = accounts.filter(a => a.hasAccountDriver).length;

  return (
    <div className={`${isPoppedOut ? 'w-full min-h-screen' : 'w-[420px] border-l'} border-slate-200 bg-white flex flex-col`}>
      {/* Panel Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800">{departmentName}</h3>
          <div className="flex items-center gap-0.5">
            {onTogglePopout && (
              <button
                onClick={onTogglePopout}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
                title={isPoppedOut ? 'Bring pane back into the workspace' : 'Pop out to a separate window'}
              >
                {isPoppedOut ? <PanelRightClose size={16} className="text-slate-400" /> : <ExternalLink size={16} className="text-slate-400" />}
              </button>
            )}
            {!isPoppedOut && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X size={18} className="text-slate-400" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>{parentSection} Department • {accounts.length} accounts</span>
        </div>
        {departmentDriver && (
          <div className="flex items-center gap-2 mt-3">
            <DriverBadge driver={departmentDriver.driverType as DriverType} />
            <span className="text-xs text-slate-400">
              {inheritingCount} inheriting, {overriddenCount} with overrides
            </span>
          </div>
        )}
        {!departmentDriver && !isLoading && (
          <div className="text-xs text-slate-400 mt-3">
            No department driver set
          </div>
        )}
      </div>

      {/* Panel Tabs */}
      <div className="flex" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setDetailPanelTab(tab)}
            className="flex-1 py-2.5 text-sm font-medium capitalize transition-colors border-b-2"
            style={detailPanelTab === tab
              ? { color: 'var(--lodge-orange-500)', borderBottomColor: 'var(--lodge-orange-400)', marginBottom: -1 }
              : { color: 'var(--lodge-onyx-500)', borderBottomColor: 'transparent', marginBottom: -1 }}
            onMouseEnter={e => { if (detailPanelTab !== tab) (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-800)'; }}
            onMouseLeave={e => { if (detailPanelTab !== tab) (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-500)'; }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <>
            <div className="rn-sk-classic-only flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
            <div className="rn-sk-inset-only"><PanelSkeleton /></div>
          </>
        ) : detailPanelTab === 'driver' ? (
          <DriverTabContent
            departmentDriver={departmentDriver}
            onDriverSelect={handleDriverSelect}
            onClearDriver={handleClearDriver}
            accounts={accounts}
            isReadOnly={isReadOnly}
            isSaving={isSaving}
            localLeadDays={localLeadDays}
            localFallbackDriver={localFallbackDriver}
            onLeadDaysChange={handleLeadDaysChange}
            onFallbackChange={handleFallbackChange}
            hasUnsavedConfig={hasUnsavedConfig}
            onSaveConfig={handleSaveConfig}
            roomForecastSources={roomForecastSources}
            localForecastSource={localForecastSource}
            onForecastSourceChange={handleForecastSourceChange}
            driverInput={driverInput}
            onDriverInputChange={handleDriverInputChange}
            revenueDepartments={revenueDepartments}
            selectedDepartments={selectedDepartments}
            onSelectedDepartmentsChange={handleSelectedDepartmentsChange}
            isLoadingDepartments={isLoadingDepartments}
            onRevenueForecastSetup={onRevenueForecastSetup}
            onAiRoomsForecast={parentSection === 'Revenue' && !isReadOnly && !isBudgetVersion ? () => setShowAiRoomsForecast(true) : undefined}
            parentSection={parentSection}
          />
        ) : detailPanelTab === 'analysis' ? (
          orgId && activeMonth ? (
            <DepartmentAnalysisTab
              departmentName={departmentName}
              chartOfAccountDepartmentId={departmentId}
              accountType={parentSection}
              orgId={orgId}
              locationId={locationId}
              versionId={versionId}
              forecastMonth={activeMonth}
              forecastYear={year}
              driverType={departmentDriver?.driverType}
              initialSelectedDepartments={selectedDepartments}
              occupiedRoomsPerMonth={occupiedRoomsPerMonth}
              currency={currency}
            />
          ) : (
            <div className="text-center py-6 text-sm text-slate-400">
              Select a month to view analysis
            </div>
          )
        ) : detailPanelTab === 'notes' ? (
          <DepartmentNotesTabContent
            departmentName={departmentName}
            accountType={parentSection}
          />
        ) : (
          <div className="text-center py-6 text-sm text-slate-400">
            {detailPanelTab === 'adjustments' ? (
              'Department adjustments coming soon'
            ) : detailPanelTab === 'overrides' ? (
              'Department overrides coming soon'
            ) : null}
          </div>
        )}
      </div>

      {/* Cascade Options Modal */}
      {pendingDriverType && (
        <CascadeOptionsModal
          isOpen={showCascadeModal}
          onClose={() => {
            setShowCascadeModal(false);
            setPendingDriverType(null);
          }}
          departmentName={departmentName}
          driverType={pendingDriverType}
          accounts={accounts}
          onConfirm={(options) => applyDriver(pendingDriverType, options)}
          isLoading={isSaving}
        />
      )}

      {/* AI Rooms Forecast Modal */}
      {showAiRoomsForecast && (
        <AiRoomsForecastModal
          versionId={versionId}
          departmentName={departmentName}
          departmentId={departmentId}
          currency={currency}
          onClose={() => setShowAiRoomsForecast(false)}
          onApplied={(result) => {
            setShowAiRoomsForecast(false);
            // New source now has data — refresh the source list + driver so the
            // panel shows Room Forecast · AI Rooms Forecast
            getRoomForecastSources(locationId, versionId).then(setRoomForecastSources).catch(() => {});
            fetchData();
            onDriverSaved?.();
            const accounts = result.accountsDistributed;
            const cleared = result.overridesCleared || 0;
            showAiUndoToast({
              batchId: result.batchId,
              message: `AI rooms forecast applied · ${result.written} day${result.written === 1 ? '' : 's'} across ${accounts} account${accounts === 1 ? '' : 's'}`
                + (cleared ? ` · ${cleared} override${cleared === 1 ? '' : 's'} cleared` : ''),
              revertedMessage: 'AI rooms forecast reverted',
              onReverted: () => { fetchData(); onDriverSaved?.(); },
            });
          }}
        />
      )}
    </div>
  );
}

interface RevenueDepartment {
  name: string;
  accountCount: number;
}

interface DriverTabContentProps {
  departmentDriver: DepartmentDriver | null;
  onDriverSelect: (driverType: DepartmentDriverType) => void;
  onClearDriver: () => void;
  accounts: DepartmentAccountInfo[];
  isReadOnly: boolean;
  isSaving: boolean;
  localLeadDays: number;
  localFallbackDriver: FallbackDriverType;
  onLeadDaysChange: (value: number) => void;
  onFallbackChange: (value: FallbackDriverType) => void;
  hasUnsavedConfig: boolean;
  onSaveConfig: () => void;
  // Room Forecast source selection
  roomForecastSources: RoomForecastSource[];
  localForecastSource: string;
  onForecastSourceChange: (value: string) => void;
  // Percentage driver input
  driverInput: string;
  onDriverInputChange: (value: string) => void;
  // Department selection for "% of Selected Revenue"
  revenueDepartments: RevenueDepartment[];
  selectedDepartments: string[];
  onSelectedDepartmentsChange: (departments: string[]) => void;
  isLoadingDepartments: boolean;
  onRevenueForecastSetup?: () => void;
  onAiRoomsForecast?: () => void;
  parentSection: 'Revenue' | 'Expense';
}

function DriverTabContent({
  departmentDriver,
  onDriverSelect,
  onClearDriver,
  accounts,
  isReadOnly,
  isSaving,
  localLeadDays,
  localFallbackDriver,
  onLeadDaysChange,
  onFallbackChange,
  hasUnsavedConfig,
  onSaveConfig,
  roomForecastSources,
  localForecastSource,
  onForecastSourceChange,
  driverInput,
  onDriverInputChange,
  revenueDepartments,
  selectedDepartments,
  onSelectedDepartmentsChange,
  isLoadingDepartments,
  onRevenueForecastSetup,
  onAiRoomsForecast,
  parentSection,
}: DriverTabContentProps) {
  const { state: { currentVersion } } = useForecastV3();
  const applicableDriverOptions = DEPARTMENT_DRIVER_OPTIONS.filter(option => {
    const applicable = DRIVER_CONFIG[option.type]?.applicableTo ?? 'both';
    return applicable === 'both' || applicable === parentSection.toLowerCase();
  });
  const isPercentageDriver = departmentDriver && PERCENTAGE_DRIVERS.includes(departmentDriver.driverType);
  const isSelectedRevenueDriver = departmentDriver?.driverType === '% of Selected Revenue';
  const isOtbRmsDriver = departmentDriver && ['Room Forecast', 'OTB Forecast', 'RMS Forecast'].includes(departmentDriver.driverType);
  // An applied AI Rooms Forecast is stored as Room Forecast reading this
  // version's lodgeic:ai_pickup_v<id> source — show it as its own choice
  const isAiRoomsActive = departmentDriver?.driverType === 'Room Forecast'
    && /^lodgeic:ai_pickup/i.test(departmentDriver.forecastSource || '');

  // Toggle a single department selection
  const toggleDepartment = (deptName: string) => {
    if (selectedDepartments.includes(deptName)) {
      onSelectedDepartmentsChange(selectedDepartments.filter(d => d !== deptName));
    } else {
      onSelectedDepartmentsChange([...selectedDepartments, deptName]);
    }
  };

  // Select/deselect all departments
  const toggleAllDepartments = () => {
    if (selectedDepartments.length === revenueDepartments.length) {
      onSelectedDepartmentsChange([]);
    } else {
      onSelectedDepartmentsChange(revenueDepartments.map(d => d.name));
    }
  };
  return (
    <div className="space-y-6">
      {/* Driver Selection */}
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-3">Select Driver</h4>
        <div className="space-y-2">
          {/* Rooms Forecast — opens Revenue Forecast modal; only shown for revenue departments */}
          {onRevenueForecastSetup && (
            <button
              onClick={() => onRevenueForecastSetup()}
              disabled={isReadOnly || isSaving}
              className="w-full p-3 rounded-lg border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm text-slate-800 flex items-center gap-1.5">
                    <TrendingUp size={14} style={{ color: 'var(--lodge-orange-400)' }} />
                    Rooms Forecast
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Enter ADR &amp; room nights by month with optional daily breakdown
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
              </div>
            </button>
          )}
          {/* AI Rooms Forecast — opens the OTB + pickup preview modal; only shown for revenue departments */}
          {onAiRoomsForecast && (
            <button
              onClick={() => onAiRoomsForecast()}
              disabled={isReadOnly || isSaving}
              className={`w-full p-3 rounded-lg border-2 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                isAiRoomsActive ? '' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
              style={isAiRoomsActive ? { borderColor: 'var(--lodge-orange-400)', background: 'var(--lodge-orange-50)' } : undefined}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm text-slate-800 flex items-center gap-1.5">
                    <Sparkles size={14} style={{ color: 'var(--lodge-orange-400)' }} />
                    AI Rooms Forecast
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {isAiRoomsActive
                      ? 'Active · refreshes daily from new on-the-books data. Open to review or change months'
                      : <>On the books + learned pickup, applied as this department&apos;s Room Forecast source</>}
                  </div>
                </div>
                {isAiRoomsActive
                  ? <Check size={16} className="flex-shrink-0" style={{ color: 'var(--lodge-orange-400)' }} />
                  : <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
              </div>
            </button>
          )}
          {applicableDriverOptions.map((option) => {
            const isSelected = displayDriverType(departmentDriver?.driverType) === option.type
              && !(isAiRoomsActive && option.type === 'Room Forecast');
            return (
              <button
                key={option.type}
                onClick={() => onDriverSelect(option.type)}
                disabled={isReadOnly || isSaving}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? ''
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={isSelected ? { borderColor: 'var(--lodge-orange-400)', background: 'var(--lodge-orange-50)' } : undefined}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm text-slate-800">
                      {getVersionDriverEntry(option.type, currentVersion?.type === 'Budget', currentVersion?.budgetReferenceYear)?.label || option.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {getVersionDriverEntry(option.type, currentVersion?.type === 'Budget', currentVersion?.budgetReferenceYear)?.desc || option.description}
                    </div>
                  </div>
                  {isSelected && (
                    <Check size={16} className="flex-shrink-0" style={{ color: 'var(--lodge-orange-400)' }} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Percentage Driver Configuration */}
      {isPercentageDriver && (
        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700">Percentage Configuration</h4>

          <div>
            <label className="text-xs text-slate-500 block mb-1.5">
              Percentage (%)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={driverInput}
                onChange={(e) => onDriverInputChange(e.target.value)}
                placeholder="e.g., 10 for 10%"
                disabled={isReadOnly || isSaving}
                className="w-24 px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:outline-none disabled:opacity-50"
              />
              <span className="text-sm text-slate-600">% distributed proportionally</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              This percentage will be divided among accounts based on past performance.
            </p>
          </div>

          {/* Department Selection for "% of Selected Revenue" */}
          {isSelectedRevenueDriver && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-500">
                  Revenue Departments to Include
                </label>
                {revenueDepartments.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAllDepartments}
                    disabled={isReadOnly || isSaving}
                    className="text-xs disabled:opacity-50" style={{ color: 'var(--lodge-orange-500)' }}
                  >
                    {selectedDepartments.length === revenueDepartments.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              {isLoadingDepartments ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={16} className="animate-spin text-slate-400" />
                  <span className="ml-2 text-sm text-slate-400">Loading departments...</span>
                </div>
              ) : revenueDepartments.length === 0 ? (
                <div className="text-xs text-slate-400 py-2">
                  No revenue departments found
                </div>
              ) : (
                <>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded bg-white">
                    {revenueDepartments.map((dept) => (
                      <label
                        key={dept.name}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${
                          isReadOnly || isSaving ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDepartments.includes(dept.name)}
                          onChange={() => toggleDepartment(dept.name)}
                          disabled={isReadOnly || isSaving}
                          className="rounded border-slate-300"
                          style={{ accentColor: 'var(--lodge-orange-400)' }}
                        />
                        <span className="flex-1 text-sm text-slate-700">{dept.name}</span>
                        <span className="text-xs text-slate-400">{dept.accountCount} accounts</span>
                      </label>
                    ))}
                  </div>
                  {selectedDepartments.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                      <AlertCircle size={12} />
                      No departments selected - will use all revenue
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Save Button */}
          {hasUnsavedConfig && !isReadOnly && (
            <button
              onClick={onSaveConfig}
              disabled={isSaving || !driverInput}
              className="w-full py-2 px-4 text-sm font-medium rounded disabled:opacity-50 transition-all"
              style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
          )}
        </div>
      )}

      {/* OTB/RMS Configuration - only show for OTB/RMS drivers */}
      {isOtbRmsDriver && (
        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700">Forecast Source, Lead Time & Fallback</h4>

          {/* Forecast Source */}
          <div>
            <label className="text-xs text-slate-500 block mb-1.5">
              Forecast source
            </label>
            <select
              value={localForecastSource}
              onChange={(e) => onForecastSourceChange(e.target.value)}
              disabled={isReadOnly || isSaving}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none disabled:opacity-50"
            >
              {roomForecastSources.map((src) => (
                <option key={src.value} value={src.value}>
                  {src.label} — {src.description}
                </option>
              ))}
            </select>
          </div>

          {/* Lead Days */}
          <div>
            <label className="text-xs text-slate-500 block mb-1.5">
              Start using Room Forecast
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={400}
                value={localLeadDays}
                onChange={(e) => onLeadDaysChange(Math.max(0, Math.min(400, parseInt(e.target.value) || 0)))}
                disabled={isReadOnly || isSaving}
                className="w-16 px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:outline-none disabled:opacity-50"
              />
              <span className="text-sm text-slate-600">days before month starts</span>
            </div>
          </div>

          {/* Fallback Driver */}
          <div>
            <label className="text-xs text-slate-500 block mb-1.5">
              For future months (outside lead time)
            </label>
            <select
              value={localFallbackDriver || ''}
              onChange={(e) => onFallbackChange(e.target.value === '' ? null : e.target.value as FallbackDriverType)}
              disabled={isReadOnly || isSaving}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:outline-none disabled:opacity-50"
            >
              {FALLBACK_DRIVER_OPTIONS.map((option) => (
                <option key={option.label} value={option.value || ''}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          {/* Save Button */}
          {hasUnsavedConfig && !isReadOnly && (
            <button
              onClick={onSaveConfig}
              disabled={isSaving}
              className="w-full py-2 px-4 text-sm font-medium rounded disabled:opacity-50 transition-all"
              style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
          )}
        </div>
      )}

      {/* Clear Driver */}
      {departmentDriver && !isReadOnly && (
        <button
          onClick={onClearDriver}
          disabled={isSaving}
          className="text-sm text-slate-500 hover:text-red-600 transition-colors"
        >
          Clear department driver
        </button>
      )}

      {/* Account Status */}
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-3">Child Accounts</h4>
        <div className="space-y-2 max-h-64 overflow-auto">
          {accounts.map((account) => (
            <div
              key={account.chartOfAccountId}
              className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
            >
              <div className="min-w-0">
                <div className="text-sm text-slate-700 truncate">
                  {account.accountName}
                </div>
                <div className="text-xs text-slate-400">
                  {account.accountNumber}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {account.hasAccountDriver ? (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                    {account.accountDriverType}
                  </span>
                ) : departmentDriver ? (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1">
                    <ChevronRight size={10} />
                    Inheriting
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">No driver</span>
                )}
              </div>
            </div>
          ))}
          {accounts.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-4">
              No accounts in this department
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
        <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          {isPercentageDriver ? (
            <>
              The percentage is distributed proportionally among child accounts based on
              past performance (Revenue accounts use current revenue, Expense accounts use
              LY actuals). Accounts with their own driver override the department driver.
            </>
          ) : (
            <>
              OTB/RMS forecasts are distributed proportionally to child accounts based on
              current revenue. For future months (outside lead time), the fallback driver
              is used at account-level. Accounts with their own driver override both.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

// Notes tab content for departments
interface DepartmentNotesTabContentProps {
  departmentName: string;
  accountType: 'Revenue' | 'Expense';
}

function DepartmentNotesTabContent({ departmentName, accountType }: DepartmentNotesTabContentProps) {
  const { state } = useForecastV3();
  const { orgId, locationId, currentVersion, year } = state;
  const { userSettings } = useUserSettingsContext() as { userSettings: { email?: string } | null };
  const currentUserEmail = userSettings?.email || '';

  // Import the events components and hook dynamically to avoid circular deps
  const { EventTimeline, NoteComposer } = require('./notes');
  const { useDepartmentEvents } = require('../../hooks/useAccountEvents');

  const versionId = currentVersion?.id;

  const {
    events,
    isLoading,
    isError,
    addNote,
    deleteEvent,
    isAddingNote,
    isDeletingEvent,
  } = useDepartmentEvents({
    orgId: orgId || 0,
    locationId: locationId || 0,
    versionId: versionId || 0,
    departmentName,
    accountType,
    year,
    enabled: !!orgId && !!locationId && !!versionId,
  });

  // Auto-scroll to bottom when events load or new event added
  const timelineRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Scroll to bottom when events first load or when new events are added
    if (timelineRef.current && events.length > 0) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  }, [events.length, isLoading]);

  const handleAddNote = async (content: string) => {
    try {
      await addNote(content);
      toast.success('Note added');
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent(eventId);
      toast.success('Note deleted');
    } catch (err) {
      console.error('Failed to delete event:', err);
      toast.error('Failed to delete note');
    }
  };

  if (!versionId) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
        Select a version to view notes
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Timeline (scrollable) - chronological order, newest at bottom */}
      <div className="flex-1 overflow-y-auto pt-3" ref={timelineRef}>
        <EventTimeline
          events={events}
          isLoading={isLoading}
          isError={isError}
          onDeleteEvent={handleDeleteEvent}
          isDeletingEvent={isDeletingEvent}
          emptyMessage="No activity yet. Add a note or make changes to see the history."
          currentUserEmail={currentUserEmail}
          chronological
        />
      </div>

      {/* Note composer at bottom */}
      <div className="pt-3 border-t border-slate-100">
        <NoteComposer
          onSubmit={handleAddNote}
          isSubmitting={isAddingNote}
          placeholder="Add a note about this department..."
        />
      </div>
    </div>
  );
}

export default DepartmentDetailPanel;
